import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useGetProjectQuery,
  usePatchProjectMutation,
  useGetBoardCompaniesQuery,
  useGetBoardContactsQuery,
  useGetSalesMainQuery,
  useGetSaleTypesQuery,
  useGetSalesRepsQuery,
  usePatchSalesMainMutation,
  useAddSalesRepMutation,
} from 'app/services/projectBoardApi';
import type { IContact, ISalesCeleryData } from 'models';
import { useTabAccess } from '../../hooks/useProjectBoardData';
import { ajaxPostToCelery, type CeleryWait } from '../../celeryRoundTrip';
import SalesNumberInput from './SalesNumberInput';
import SaleTypePopup from './SaleTypePopup';
import RepUnitRow from './RepUnitRow';

/**
 * SALES TAB (`#sales` — `sales/view/sales.js` + sales.tmpl live part),
 * inventory §3.9 / parity 68–87.
 *
 * Boot: waits for sales + sale-types + reps (+ project/companies/contacts),
 * then shows the dotted preloader and runs the celery round trip
 * (POST /reports/sales_project → {task_id} → socket wait w/ emit-backoff);
 * `msg.data.sales_data` feeds the read-only figures and `msg.data.generate_id`
 * is attached to EVERY subsequent sales/rep PATCH (risk 3 — without it the
 * server refuses to persist amount/commission fields). RECALC reruns the
 * whole round trip (parity 69).
 *
 * SALE-TYPE (NON-)PERSISTENCE — risk 1 / Open Question #6, replicated
 * EXACTLY: `SalesMainModel.onchange` is empty, so changing the Sale Type only
 * `set`s the model locally — NO sales PATCH carries `sale_type_id`. From
 * "Direct" (id 1) the confirm popup applies the same local set; between
 * non-direct types it applies immediately. The UI re-renders with the new
 * type (commissions/reps sections appear), subsequent PATCH echoes do NOT
 * clobber it (the server echoes only the sent fields), and a page reload
 * reverts to the server value. We do NOT invent persistence.
 *   - DELIBERATE NON-REPLICATION: the legacy `.dashboard_sync` change also
 *     fires `projectMainModel.trigger('change')`, whose autosave hook re-PATCHes
 *     the project's stale `changed` hash (a semantically empty echo write).
 *     Replicating it would be pure write amplification (risk 8) — skipped.
 *
 * The tab rail item is hidden by shared CSS on BOTH sides
 * (`#menu-nav a[value=sales]{display:none}` in project/styles.css) — the tab
 * is reachable by deep-link `#sales` only. Reproduced by loading the same
 * stylesheet; deliberately NOT "fixed".
 *
 * [NEG] honored: `view.sales_commission` dangling module + commented legacy
 * markup (parity 87); the sale-type radio re-check (parity 86).
 */

/** changeInput value coercion: ''→null, has-letters→raw, numeric→decoded number. */
const parseInputValue = (raw: string): string | number | null => {
  if (raw === '') return null;
  if (/[a-z]/i.test(raw)) return raw;
  const n = Number(raw.replace(/,/g, '').trim());
  return Number.isNaN(n) ? raw : n;
};

/** Handlebars-truthiness fallback ({{#if main.x}}…{{else}}celery{{/if}}). */
const orCelery = (
  modelValue: number | string | null | undefined,
  celeryValue: number | string | null | undefined
): number | string | null | undefined => (modelValue ? modelValue : celeryValue);

interface CeleryState {
  pending: boolean;
  generateId: string | null;
  fromCelery: ISalesCeleryData | null;
}

const SalesTab = ({ projectId }: { projectId: number }) => {
  const { data: project } = useGetProjectQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const { data: companies } = useGetBoardCompaniesQuery();
  const { data: contacts } = useGetBoardContactsQuery();
  const { data: salesMain } = useGetSalesMainQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const { data: saleTypes } = useGetSaleTypesQuery();
  const { data: reps } = useGetSalesRepsQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const access = useTabAccess(projectId, 'sales');

  const [patchProject] = usePatchProjectMutation();
  const [patchSalesMain] = usePatchSalesMainMutation();
  const [addSalesRep] = useAddSalesRepMutation();

  // ---- celery round trip (pre_render) ----
  const [celery, setCelery] = useState<CeleryState>({
    pending: true,
    generateId: null,
    fromCelery: null,
  });
  const waitRef = useRef<CeleryWait | null>(null);
  const bootedRef = useRef(false);

  const runRecalc = useCallback(() => {
    waitRef.current?.cancel();
    setCelery((c) => ({ ...c, pending: true }));
    const wait = ajaxPostToCelery('/reports/sales_project', { project_id: projectId });
    waitRef.current = wait;
    wait.promise
      .then((msg) => {
        const data = (msg.data ?? {}) as {
          sales_data?: ISalesCeleryData;
          generate_id?: string;
        };
        setCelery({
          pending: false,
          generateId: data.generate_id ?? null,
          fromCelery: data.sales_data ?? null,
        });
      })
      .catch(() => undefined); // failed POST: preloader stays (legacy parity)
  }, [projectId]);

  const fetched = !!(project && companies && contacts && salesMain && saleTypes && reps);
  useEffect(() => {
    if (fetched && !bootedRef.current) {
      bootedRef.current = true;
      runRecalc();
    }
  }, [fetched, runRecalc]);
  useEffect(() => () => waitRef.current?.cancel(), []);

  // ---- sale-type LOCAL override (non-persisted — see header comment) ----
  const [saleTypeOverride, setSaleTypeOverride] = useState<number | null>(null);
  const [typePopup, setTypePopup] = useState<{
    newId: number;
    checked: string;
    changeChecked: string;
  } | null>(null);

  // Contacts grouped by company (_.groupBy(floor(company_id))).
  const companyContacts = useMemo(() => {
    const map = new Map<number, IContact[]>();
    (contacts ?? []).forEach((c) => {
      const key = Math.floor(Number(c.company_id));
      const list = map.get(key);
      if (list) list.push(c);
      else map.set(key, [c]);
    });
    return map;
  }, [contacts]);

  if (!fetched) return null; // legacy: #sales stays empty until the 3 fetches
  if (celery.pending) {
    return <div className="col-xs-12 dotted-preloader active" />;
  }

  const main = salesMain!;
  const fromCelery = celery.fromCelery;
  const generateId = celery.generateId;
  const locked = !access; // parity 84: every input/button/select disabled

  const effectiveTypeId = saleTypeOverride ?? Number(main.sale_type_id ?? 0);
  const isType3 = effectiveTypeId === 3;
  const showReps = effectiveTypeId !== 1; // {{#ifCompare main.sale_type_id '!=' 1}}

  // ---- changeInput (sales model PATCH + reps refetch via tag invalidation) ----
  const commitSalesField = (name: string, value: string | number | null) => {
    patchSalesMain({
      projectId,
      body: { [name]: value, generate_id: generateId },
    });
  };

  // ---- changeSelect (NON sale-type selects live on the PROJECT model) ----
  const commitProjectSelect = (name: string, raw: string) => {
    const v = raw === '' || raw === 'None' ? NaN : parseInt(raw, 10);
    patchProject({ id: projectId, body: { [name]: Number.isNaN(v) ? null : v } });
  };

  // ---- changeSaleType ----
  const onSaleTypeChange = (raw: string) => {
    const newId = parseInt(raw, 10);
    if (Number.isNaN(newId) || newId === effectiveTypeId) return;
    if (effectiveTypeId === 1) {
      const checked = saleTypes!.find((t) => t.id === effectiveTypeId);
      const changeChecked = saleTypes!.find((t) => t.id === newId);
      setTypePopup({
        newId,
        checked: checked?.name ?? '',
        changeChecked: changeChecked?.name ?? '',
      });
    } else {
      setSaleTypeOverride(newId);
    }
  };

  // ---- + REP gating: ordering-rep co or the LAST rep's co unset (parity 80) ----
  const orderingRepUnset = project!.ordering_rep_co_id == null;
  const lastRepUnset = reps!.length > 0 && reps![reps!.length - 1].rep_co_id == null;
  const addRepGated = orderingRepUnset || lastRepUnset;

  const onAddRep = () => {
    if (locked || addRepGated) return;
    addSalesRep({ sales_id: projectId });
  };

  const customerCoId = project!.customer_co_id as number | null | undefined;
  const customerContacts =
    customerCoId != null ? companyContacts.get(Number(customerCoId)) ?? [] : [];
  const orderingRepCoId = project!.ordering_rep_co_id as number | null | undefined;
  const orderingRepContacts =
    orderingRepCoId != null ? companyContacts.get(Number(orderingRepCoId)) ?? [] : [];

  return (
    <div className="content col-xs-12">
      <div className="content-item  row sales-inputs">
        <div className="sales-order col-xs-9">
          <div className="project-sales-tab-heading ">
            <div className="col-xs-12">
              <h2 className="heading">Sales Order</h2>
            </div>
          </div>
          <div className="project-sales-tab-content ">
            <div className="col-xs-6">
              <div className="row padding-row">
                <div className="col-xs-5">
                  <span className="name">Purchase Order No.</span>
                </div>
                <div className="col-xs-5 col-xs-offset-2">
                  <PoNumberInput
                    value={(main.po_number as string | null | undefined) ?? ''}
                    disabled={locked}
                    onCommit={(raw) => commitSalesField('po_number', parseInputValue(raw))}
                  />
                </div>
              </div>
              <div className="row  padding-row">
                <div className="col-xs-5">
                  <span className="name">Sale Amount to Customer</span>
                </div>
                <div className="col-xs-2 text-right no-right-padding">
                  <span className="unit">US$</span>
                </div>
                <div className="col-xs-5">
                  <SalesNumberInput
                    name="sell_amount_to_customer"
                    value={main.sell_amount_to_customer}
                    formatter="integer"
                    className="xs-input input-number"
                    min={0}
                    max={100000000}
                    step="1"
                    placeholder="- - -"
                    disabled={locked}
                    onCommit={commitSalesField}
                  />
                </div>
              </div>
              <div className="row  padding-row">
                <div className="col-xs-5">
                  <span className="name">Sale Type</span>
                </div>
                <div className="col-xs-5 col-xs-offset-2">
                  <select
                    className="medium-input dashboard_sync"
                    name="sale_type_id"
                    required
                    disabled={locked}
                    value={String(effectiveTypeId)}
                    onChange={(e) => onSaleTypeChange(e.target.value)}
                  >
                    {saleTypes!.map((t) => (
                      <option key={t.id} value={String(t.id)} data-name={t.name}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {isType3 && (
                <div className="row  padding-row">
                  <div className="col-xs-5">
                    <span className="name">Total Rep Commission</span>
                  </div>
                  <div className="col-xs-2 text-right no-right-padding">
                    <span className="unit">%</span>
                  </div>
                  <div className="col-xs-2">
                    <SalesNumberInput
                      name="total_rep_commission_percent"
                      value={orCelery(
                        main.total_rep_commission_percent,
                        fromCelery?.total_rep_commission_percent
                      )}
                      formatter="decimal"
                      className="xxs-input input-number"
                      step="0.01"
                      readOnly
                      disabled={locked}
                    />
                  </div>
                  <div className="col-xs-1 text-right no-right-padding">
                    <span className="unit">US$</span>
                  </div>
                  <div className="col-xs-2">
                    <SalesNumberInput
                      name="total_rep_commission"
                      value={orCelery(
                        main.total_rep_commission,
                        fromCelery?.total_rep_commission
                      )}
                      formatter="integer"
                      className="xs-input input-number"
                      step="1"
                      readOnly
                      disabled={locked}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="col-xs-6">
              <div className="row padding-row">
                <div className="col-xs-5">
                  <span className="name">Customer Co.</span>
                </div>
                <div className="col-xs-5 col-xs-offset-2">
                  <select
                    className="medium-input dashboard_sync"
                    name="customer_co_id"
                    required
                    disabled={locked}
                    value={customerCoId != null ? String(customerCoId) : ''}
                    onChange={(e) => commitProjectSelect('customer_co_id', e.target.value)}
                  >
                    <option value="">-----</option>
                    {companies!.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="row padding-row">
                <div className="col-xs-5">
                  <span className="name">Contact</span>
                </div>
                <div className="col-xs-5 col-xs-offset-2">
                  <select
                    className="medium-input dashboard_sync"
                    name="customer_contact_id"
                    required
                    disabled={locked}
                    value={
                      project!.customer_contact_id != null
                        ? String(project!.customer_contact_id)
                        : ''
                    }
                    onChange={(e) => commitProjectSelect('customer_contact_id', e.target.value)}
                  >
                    <option value="">-----</option>
                    {customerContacts.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="row padding-row">
                <div className="col-xs-5">
                  <span className="name">Project Actual Net Price</span>
                </div>
                <div className="col-xs-2 text-right no-right-padding">
                  <span className="unit">US$</span>
                </div>
                <div className="col-xs-5">
                  <SalesNumberInput
                    name="mfg_price_actual"
                    value={fromCelery?.price_net_actual}
                    formatter="integer"
                    className="xs-input input-number"
                    min={0}
                    max={10000000}
                    step="1"
                    readOnly
                    disabled={locked}
                  />
                </div>
              </div>
            </div>
          </div>
          {showReps && (
            <>
              <div className=" project-sales-tab-header-table padding-row">
                <div className="col-xs-12">
                  <div className="header-table">
                    <div className="row">
                      <div className="col-xs-3">
                        <h3>Representative Company</h3>
                      </div>
                      <div className="col-xs-3">
                        <h3>Contact</h3>
                      </div>
                      {isType3 && (
                        <div className="col-xs-4 text-right">
                          <h3>Commission</h3>
                        </div>
                      )}
                      <div className={`  ${isType3 ? 'col-xs-2' : 'col-xs-6'} text-right`}>
                        <button
                          type="button"
                          id="addRep"
                          className={`btn-add ${addRepGated ? ' non-clickable' : ''}`}
                          tabIndex={-1}
                          disabled={locked}
                          onClick={onAddRep}
                        >
                          <span>+ REP</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xs-12" id="RepUnits">
                {/* Hardcoded FIRST row — the ORDERING rep (partner fields on the
                    PROJECT; commission pair on the SALES model). */}
                <div className="rep project-sales-tab-content-table padding-row row">
                  <div className="col-xs-3">
                    <select
                      className="medium-input"
                      name="ordering_rep_co_id"
                      required
                      disabled={locked}
                      value={orderingRepCoId != null ? String(orderingRepCoId) : 'None'}
                      onChange={(e) => commitProjectSelect('ordering_rep_co_id', e.target.value)}
                    >
                      <option value="None">- -</option>
                      {companies!.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-xs-3">
                    <select
                      className="small-input"
                      name="ordering_rep_contact_id"
                      required
                      disabled={locked}
                      value={
                        project!.ordering_rep_contact_id != null
                          ? String(project!.ordering_rep_contact_id)
                          : 'None'
                      }
                      onChange={(e) =>
                        commitProjectSelect('ordering_rep_contact_id', e.target.value)
                      }
                    >
                      <option value="None">- -</option>
                      {orderingRepContacts.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {isType3 && (
                    <div className="col-xs-4">
                      <div className="row">
                        <div className="col-xs-2 text-right no-right-padding">
                          <span className="unit">%</span>
                        </div>
                        <div className="col-xs-4">
                          <SalesNumberInput
                            name="ordering_rep_co_commission_percent"
                            value={main.ordering_rep_co_commission_percent}
                            formatter="decimal"
                            className="xxs-input input-number"
                            step="0.01"
                            min={0}
                            max={100}
                            disabled={locked}
                            onCommit={commitSalesField}
                          />
                        </div>
                        <div className="col-xs-2 text-right no-right-padding">
                          <span className="unit">US$</span>
                        </div>
                        <div className="col-xs-4">
                          <SalesNumberInput
                            name="ordering_rep_co_commission_amount"
                            value={main.ordering_rep_co_commission_amount}
                            formatter="integer"
                            className="xs-input input-number"
                            step="1"
                            min={0}
                            max={10000000}
                            disabled={locked}
                            onCommit={commitSalesField}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {reps!.map((rep) => (
                  <RepUnitRow
                    key={rep.id}
                    rep={rep}
                    salesId={projectId}
                    generateId={generateId}
                    companies={companies!}
                    companyContacts={companyContacts}
                    isType3={isType3}
                    locked={locked}
                  />
                ))}
              </div>
            </>
          )}
        </div>
        <div className="col-xs-3 reload_button">
          <button
            id="refresh_sales"
            className="btn-add right"
            disabled={locked}
            onClick={runRecalc}
          >
            RECALC
          </button>
        </div>
      </div>
      {typePopup && (
        <SaleTypePopup
          checked={typePopup.checked}
          changeChecked={typePopup.changeChecked}
          onConfirm={() => {
            // confirmChange: LOCAL set only (no PATCH — see header comment).
            setSaleTypeOverride(typePopup.newId);
            setTypePopup(null);
          }}
          onClose={() => setTypePopup(null)}
        />
      )}
    </div>
  );
};

/**
 * Purchase Order No. input (2–15 chars + `.text-clear` icon). Commits on
 * change (blur-if-changed); the clear icon clears AND commits (MainView
 * clearTexField triggers change+blur — parity 26/70).
 */
const PoNumberInput = ({
  value,
  disabled,
  onCommit,
}: {
  value: string;
  disabled: boolean;
  onCommit: (raw: string) => void;
}) => {
  const [draft, setDraft] = useState(value ?? '');
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (document.activeElement !== ref.current) setDraft(value ?? '');
  }, [value]);

  const commitIfChanged = (v: string) => {
    if (v !== (value ?? '')) onCommit(v);
  };

  return (
    <>
      <input
        ref={ref}
        autoComplete="off"
        className="xs-input"
        type="text"
        minLength={2}
        maxLength={15}
        name="po_number"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commitIfChanged(draft)}
      />
      <i
        className="text-clear icon-close-1"
        onClick={() => {
          if (disabled) return;
          setDraft('');
          commitIfChanged('');
        }}
      />
    </>
  );
};

export default SalesTab;
